import { useQuery, useQueryClient } from '@tanstack/react-query';
import remoteDbService from '@/core/database/RemoteDBService';
import { type Page } from '@/features/pages/types/page.types';
import { publicKeys } from '@/shared/query/queryKeys';

export const usePublicCollectionNote = (
  pageId: string | undefined,
  collectionId: string | undefined
) => {
  const queryClient = useQueryClient();

  const getCachedPage = (): Page | undefined => {
    if (!pageId || !collectionId) return undefined;
    const fullPages = queryClient.getQueryData<Page[]>(
      publicKeys.collectionNotesFull(collectionId)
    );
    const cachedPage = fullPages?.find(p => p.id === pageId);
    return cachedPage?.content !== undefined ? cachedPage : undefined;
  };

  return useQuery<Page | null>({
    queryKey: publicKeys.note(pageId, collectionId),
    queryFn: async () => {
      if (!pageId) return null;

      // Reuse the collection's already-downloaded full pages when available
      const cachedPage = getCachedPage();
      if (cachedPage) return cachedPage;

      // Fall back to fetching the individual page
      return remoteDbService.getPublicNote(pageId, collectionId);
    },
    // Seed synchronously from the collection's full-content cache so switching
    // pages within a collection never renders a loading state.
    initialData: getCachedPage,
    enabled: !!pageId,
  });
};
